<template>
  <div class="h-full">
    <Menu v-if="!isEmpty(items) && selected" class="focus:outline-none">
      <MenuItems static class="w-full overflow-auto focus:outline-none">
        <MenuItem
          v-for="item in items"
          v-slot="{ active }"
          :key="item.id"
          :class="[selected.id == item.id ? 'border-l-4 border-l-primary-80' : ' padding-l-4']"
          class="group flex w-full cursor-pointer items-center border focus:outline-none focus:ring-0"
          @click="updateSelected(item.id)"
        >
          <div
            :class="[
              selected.id == item.id ? 'bg-primary-99' : active ? ' bg-neutral-100' : 'bg-white hover:bg-neutral-100',
            ]"
            class="relative flex w-full items-center"
          >
            <div class="flex items-center px-6 py-4 text-sm font-medium">
              <div
                :class="[selected.id == item.id ? 'border-primary-80' : 'border-neutral-90']"
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center self-start rounded-full border-2"
              >
                <StarIcon
                  v-if="item.type === 'autosave'"
                  :class="[selected.id == item.id ? 'text-primary-40 ' : 'text-neutral-70']"
                  class="h-6 w-6"
                  aria-hidden="true"
                />
                <ArchiveIcon
                  v-if="item.type === 'history_manual'"
                  :class="[selected.id == item.id ? 'text-primary-40 ' : 'text-neutral-70']"
                  class="h-6 w-6"
                  aria-hidden="true"
                />
                <ClockIcon
                  v-if="item.type === 'history_auto'"
                  :class="[selected.id == item.id ? 'text-primary-40 ' : 'text-neutral-70']"
                  class="h-6 w-6"
                  aria-hidden="true"
                />
              </div>
              <div
                :class="[selected.id == item.id ? 'text-primary-40' : 'text-neutral-70']"
                class="font-mediums ml-4 inline-grid text-sm"
              >
                <div>
                  <span class="mr-2 uppercase">{{ d(item.date, "shortWithTime") }}</span>
                  <SimpleBadge v-if="selected.id == item.id && item.type === 'autosave'" theme="blue" size="small">
                    {{ t("versionType.autosave") }}
                  </SimpleBadge>
                  <SimpleBadge v-if="selected.id == item.id && item.type === 'history_auto'" theme="gray" size="small">
                    {{ t("versionType.history_auto") }}
                  </SimpleBadge>
                  <SimpleBadge
                    v-if="selected.id == item.id && item.type === 'history_manual'"
                    theme="gray"
                    size="small"
                  >
                    {{ t("versionType.history_manual") }}
                  </SimpleBadge>
                </div>
                <div>
                  <p :class="[selected.id == item.id && 'mb-2']" class="text-sm font-medium">{{ item.user }}</p>
                  <transition
                    enter-active-class="transition-all transform overflow-hidden duration-200"
                    enter-from-class="max-h-0"
                    enter-to-class="max-h-10"
                    leave-active-class="transition-all transform overflow-hidden duration-200"
                    leave-from-class="max-h-10"
                    leave-to-class="max-h-0"
                  >
                    <div v-if="selected.id == item.id">
                      <SimpleButton @click="restore($event, item.id)">
                        <template #leading><CheckIcon /></template>
                        {{ t("button.restoreVersion") }}</SimpleButton
                      >
                    </div>
                  </transition>
                </div>
              </div>
            </div>
            <div class="absolute right-0 mr-8">
              <LoadingSpinner v-if="item?.loading" class="h-6 w-6 text-primary-60" />
              <ChevronRightIcon v-else-if="selected.id != item.id" class="h-5 w-5 text-neutral-70" aria-hidden="true" />
            </div>
          </div>
        </MenuItem>
      </MenuItems>
    </Menu>
    <div v-else class="h-full">
      <div class="mx-12 flex h-full flex-col justify-center text-center text-sm">
        <ClockIcon class="mx-auto h-7 w-7 text-neutral-70" aria-hidden="true" />
        <h1 class="mb-3 mt-2 text-sm font-medium text-neutral-70">{{ t("emptyState.title") }}</h1>
        <p class="text-neutral-80">{{ t("emptyState.description") }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

// Components
import { Menu, MenuItems, MenuItem } from "@headlessui/vue";
import LoadingSpinner from "@atoms/LoadingSpinner.vue";
import SimpleBadge from "@atoms/SimpleBadge";
import SimpleButton from "@atoms/SimpleButton.vue";

//Icons
import { StarIcon, ArchiveIcon, ClockIcon, CheckIcon } from "@heroicons/vue/outline";
import { ChevronRightIcon } from "@heroicons/vue/solid";

//Utils
import { isEmpty } from "lodash";

// Types
import { HistoryItems, HistoryItem } from "./VersionHistory.types";

// Props
const props = withDefaults(
  defineProps<{
    items: HistoryItems;
    selected: HistoryItem;
  }>(),
  {
    items: () => [] as HistoryItems,
    selected: () => ({}) as HistoryItem,
  },
);

// Lang
const { d, t } = useI18n();

// Emits
const emit = defineEmits<{
  (e: "update:selected", selected: HistoryItem): void;
  (e: "restore", restored: HistoryItem): void;
}>();

const findSelected = (id: string) => {
  return props.items.find((item) => item.id == id);
};

const updateSelected = (id: string) => {
  const selected = findSelected(id);
  if (!selected || selected.id == props.selected.id) return;
  emit("update:selected", selected);
};

const restore = (event: Event, id: string) => {
  event.stopPropagation();
  const selected = findSelected(id);
  if (!selected) return;
  emit("restore", selected);
};
</script>

<i18n lang="json">
{
  "es": {
    "button": {
      "restoreVersion": "Restaurar versión"
    },
    "emptyState": {
      "title": "Sin versiones para mostrar",
      "description": "Actualmente no existen versiones de esta plantilla para el filtro seleccionado. Trabaja desde el editor para que se generen y guarden en este historial."
    },
    "versionType": {
      "autosave": "Versión recuperada",
      "history_auto": "Versión automática",
      "history_manual": "Versión manual"
    }
  },
  "pt": {
    "button": {
      "restoreVersion": "Restaurar esta versão"
    },
    "emptyState": {
      "title": "Sin versiones para mostrar",
      "description": "Actualmente no existen versiones de esta plantilla para el filtro seleccionado. Trabaja desde el editor para que se generen y guarden en este historial."
    },
    "versionType": {
      "autosave": "Versão manual",
      "history_auto": "Versão automática",
      "history_manual": "Versão recuperada"
    }
  }
}
</i18n>
